import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../utils/firebase';
import { ref, onValue, set, off } from 'firebase/database';
import { doc, addDoc, collection, serverTimestamp, getDoc } from 'firebase/firestore';
import { database, db } from '../utils/firebase';
import { generateChatResponse } from '../utils/geminiAI';

const AIChatSupport = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const messagesEndRef = useRef(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (!user) return;

    let chatRef = null;
    let unsubscribe = null;

    // Check user role and only allow hosts, venues, and admins
    const checkUserRole = async () => {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const role = userData?.role;
          
          if (['host', 'venue', 'admin'].includes(role)) {
            setUserRole(role);
            
            // Initialize chat inline to avoid dependency issues
            const newChatId = `chat_${user.uid}_${Date.now()}`;
            setChatId(newChatId);

            // Listen for messages
            chatRef = ref(database, `aiChats/${newChatId}/messages`);
            unsubscribe = onValue(chatRef, (snapshot) => {
              const data = snapshot.val();
              if (data) {
                const messageList = Object.values(data).sort((a, b) => a.timestamp - b.timestamp);
                setMessages(messageList);
              }
            });

            // Add welcome message
            const welcomeMessage = {
              id: 'welcome',
              text: `Hello! I'm your Trivia Plug AI assistant. I can help you with game creation, scheduling, technical support, and platform questions. How can I assist you today?`,
              sender: 'ai',
              timestamp: Date.now(),
              type: 'welcome'
            };

            setMessages([welcomeMessage]);
          } else {
            onClose(); // Close chat for unauthorized users
          }
        } else {
          console.log('User document not found');
          onClose();
        }
      } catch (error) {
        console.error('Error checking user role:', error);
        onClose();
      }
    };

    checkUserRole();

    // Cleanup function
    return () => {
      if (chatRef && unsubscribe) {
        off(chatRef);
        unsubscribe();
      }
    };
  }, [user, onClose]);

  useEffect(() => {
    if (isOpen) {
      setMessages([]);
      setInputMessage('');
      setIsLoading(false);
    }
  }, [isOpen, onClose]);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, onClose]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || !chatId || isLoading) return;

    const userMessage = {
      id: `msg_${Date.now()}`,
      text: inputMessage,
      sender: 'user',
      timestamp: Date.now(),
      userId: user.uid,
      userRole: userRole
    };

    // Add user message to chat
    const messageRef = ref(database, `aiChats/${chatId}/messages/${userMessage.id}`);
    await set(messageRef, userMessage);

    setInputMessage('');
    setIsLoading(true);

    try {
      // Generate AI response
      const aiResponse = await generateAIChatResponse(inputMessage, userRole);
      
      const aiMessage = {
        id: `ai_${Date.now()}`,
        text: aiResponse,
        sender: 'ai',
        timestamp: Date.now(),
        type: 'response'
      };

      // Add AI response to chat
      const aiMessageRef = ref(database, `aiChats/${chatId}/messages/${aiMessage.id}`);
      await set(aiMessageRef, aiMessage);

      // Store chat session in Firestore for admin review
      await addDoc(collection(db, 'aiChatSessions'), {
        chatId,
        userId: user.uid,
        userEmail: user.email,
        userRole,
        startTime: serverTimestamp(),
        lastActivity: serverTimestamp(),
        messageCount: messages.length + 2
      });

    } catch (error) {
      console.error('Error generating AI response:', error);
      
      const errorMessage = {
        id: `error_${Date.now()}`,
        text: "I'm sorry, I'm having trouble responding right now. Please try again or contact support via email.",
        sender: 'ai',
        timestamp: Date.now(),
        type: 'error'
      };

      const errorRef = ref(database, `aiChats/${chatId}/messages/${errorMessage.id}`);
      await set(errorRef, errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const generateAIChatResponse = async (userMessage, role) => {
    try {
      const response = await generateChatResponse(userMessage, role);
      return response || "I'm here to help! What would you like to know about Trivia Plug?";
    } catch (error) {
      console.error('AI response error:', error);
      return "I'm having trouble processing your request. Please try again or contact support.";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Helper function to render HTML content safely
  const renderMessageContent = (text) => {
    // Check if the text contains HTML links
    if (text.includes('<a href=')) {
      // Use dangerouslySetInnerHTML for simple HTML rendering
      return (
        <span 
          dangerouslySetInnerHTML={{ 
            __html: text.replace(
              /class='([^']*)'/g, 
              'class="$1" style="color: #4ade80; text-decoration: underline; cursor: pointer;"'
            )
          }} 
        />
      );
    }
    
    // If no HTML links found, return the text as is
    return text;
  };

  const escalateToHuman = async () => {
    if (!chatId) return;

    try {
      // Create support ticket
      await addDoc(collection(db, 'supportTickets'), {
        userId: user.uid,
        userEmail: user.email,
        userRole: userRole,
        subject: 'AI Chat Escalation',
        message: `User escalated from AI chat. Chat ID: ${chatId}`,
        category: 'technical',
        priority: 'medium',
        status: 'open',
        createdAt: serverTimestamp(),
        source: 'ai_chat_escalation',
        chatId: chatId
      });

      const escalationMessage = {
        id: `escalate_${Date.now()}`,
        text: "I've escalated your request to our human support team. They'll get back to you via email shortly. Thank you for your patience!",
        sender: 'ai',
        timestamp: Date.now(),
        type: 'escalation'
      };

      const escalationRef = ref(database, `aiChats/${chatId}/messages/${escalationMessage.id}`);
      await set(escalationRef, escalationMessage);

    } catch (error) {
      console.error('Error escalating chat:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-gray-800 rounded-lg shadow-2xl border border-gray-700 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 p-4 rounded-t-lg border-b border-gray-700 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
          <h3 className="text-white font-semibold">AI Support</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs px-3 py-2 rounded-lg ${
                message.sender === 'user'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-200'
              }`}
            >
              <div className="text-sm">{renderMessageContent(message.text)}</div>
              {message.type === 'welcome' && (
                <p className="text-xs text-gray-300 mt-1">
                  Available for Hosts, Venues & Admins
                </p>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-700 text-gray-200 px-3 py-2 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-700">
        <div className="flex space-x-2">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1 p-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none resize-none"
            rows="2"
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded transition-colors"
          >
            Send
          </button>
        </div>
        
        {/* Escalation Button */}
        <div className="mt-2 text-center">
          <button
            onClick={escalateToHuman}
            className="text-xs text-gray-400 hover:text-green-400 transition-colors"
          >
            Need human support? Click here
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatSupport; 